import { supabase } from "./supabase";

// ─────────────────────────────────────────────────────────────────────────────
// Round 11 — the ONE client clock (R12). Every behaviour-bearing time read
// under apps/web/src goes through appNow()/appNowMs() instead of Date.now()/
// new Date() (the pin enforces this everywhere outside this file and tests).
// In production (replay_mode = 'off') app_now() === now(), so appNow() tracks
// real time; under a staging replay it tracks the server's offset clock.
//
// R13 — the epoch-driven local reset lives here too: the epoch key is
// namespaced by the Supabase host so a local stack and staging never share
// one, and the four-branch first-sync rule decides whether a sync clears the
// stale prototype state (nrl_tracker_v3, bridgeImported:*, the admin drafts
// session key) before re-fetching server-backed models.
// ─────────────────────────────────────────────────────────────────────────────

function storageHost() {
  try {
    const raw = import.meta.env.VITE_SUPABASE_URL;
    if (!raw) return "unknown";
    return new URL(raw).host || "unknown";
  } catch {
    return "unknown";
  }
}

function epochKey() {
  return `skohr_member_data_epoch:${storageHost()}`;
}

// Per-tab state. `appliedEpoch` starts UNSET (null) so the first sync in this
// tab can tell "never applied" apart from "applied epoch 0" (R13 v3 N1).
let appliedEpoch = null;
let currentAccountId = undefined; // the identity the current appliedEpoch belongs to
let payload = null; // the last get_app_clock() payload
let offsetMs = 0; // appNowMs() - Date.now() at the last successful sync
let ready = false;
let requestSerial = 0;
let resetSerial = 0;
let generation = 0; // invalidates reads on offset, epoch and identity changes
const listeners = new Set();

function notify(change) {
  for (const listener of listeners) {
    try {
      listener(payload, { ready, ...change });
    } catch (err) {
      console.warn("[appClock] listener failed", err);
    }
  }
}

function clearLocalMemberState() {
  resetSerial += 1;
  try {
    localStorage.removeItem("nrl_tracker_v3");
  } catch {
    /* ignore */
  }
  try {
    const stale = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("bridgeImported:")) stale.push(key);
    }
    stale.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem("skohr_admin_drafts");
  } catch {
    /* ignore */
  }
}

function readStoredEpoch() {
  try {
    const raw = localStorage.getItem(epochKey());
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeStoredEpoch(epoch) {
  try {
    localStorage.setItem(epochKey(), String(epoch));
  } catch {
    /* ignore */
  }
}

// The R13 v3 four-branch first-sync rule, plus the simpler later-sync rule.
// First sync in this tab (appliedEpoch === null):
//   - stored epoch present and equal to the server epoch -> adopt, no clear.
//   - no stored epoch and server epoch === 0 -> store, no clear (a fresh
//     install in a never-reset environment — production).
//   - stored epoch present and different, OR no stored epoch and server
//     epoch > 0 -> CLEAR then store.
// Every later sync in this tab: clear only when the server epoch differs from
// this tab's already-applied epoch (so another tab's storage write alone
// cannot hide a reset from this tab — only an actual differing sync can).
function applyEpoch(serverEpoch) {
  if (typeof serverEpoch !== "number" || !Number.isFinite(serverEpoch)) return;

  if (appliedEpoch === null) {
    const stored = readStoredEpoch();
    if (stored !== null && stored === serverEpoch) {
      appliedEpoch = serverEpoch;
      return;
    }
    if (stored === null && serverEpoch === 0) {
      appliedEpoch = serverEpoch;
      writeStoredEpoch(serverEpoch);
      return;
    }
    appliedEpoch = serverEpoch;
    clearLocalMemberState();
    writeStoredEpoch(serverEpoch);
    generation += 1;
    return;
  }

  if (serverEpoch !== appliedEpoch) {
    appliedEpoch = serverEpoch;
    clearLocalMemberState();
    writeStoredEpoch(serverEpoch);
    generation += 1;
  }
}

/** Calls get_app_clock() and refreshes the clock + epoch state. Safe to call
 * repeatedly; a failed sync keeps the last offset (real time if there never
 * was one) so the next trigger can retry. */
export async function syncAppClock() {
  if (!currentAccountId) return null;
  const account = currentAccountId;
  const requestGeneration = generation;
  const request = ++requestSerial;
  let data = null;
  try {
    const res = await supabase.rpc("get_app_clock");
    if (!res.error) data = res.data;
  } catch {
    data = null;
  }
  if (!data || account !== currentAccountId || requestGeneration !== generation || request !== requestSerial) return null;
  if (!Number.isFinite(new Date(data.app_now).getTime())) return null;
  if (payload?.offset_seconds !== data.offset_seconds || payload?.replay_mode !== data.replay_mode) generation += 1;
  payload = data;
  const serverNow = new Date(data.app_now).getTime();
  if (Number.isFinite(serverNow)) offsetMs = serverNow - Date.now();
  const previousReset = resetSerial;
  applyEpoch(data.member_data_epoch);
  ready = true;
  notify({ reset: resetSerial !== previousReset });
  return payload;
}

/** appNow() in milliseconds — real time until the first successful sync. */
export function appNowMs() {
  return Date.now() + offsetMs;
}

/** appNow() as a Date — the one clock every behaviour-bearing read must use. */
export function appNow() {
  return new Date(appNowMs());
}

/** The last get_app_clock() payload, or null before the first sync. */
export function getAppClock() {
  return payload;
}

/** Subscribe to every successful clock sync. Returns an unsubscribe function. */
export function onAppClock(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Readiness belongs to the current identity, independently of the fallback offset. */
export function isAppClockReady() { return ready; }

/** Snapshot before a read and compare after each await before adopting its result. */
export function getClockGeneration() {
  return generation;
}

// Account switch (R13): a different signed-in identity never inherits this
// tab's already-applied epoch — it must sync fresh before bridge import or
// any time-dependent read runs for it.
export function resetAppClockForAccount(accountId) {
  if (accountId === currentAccountId) return;
  if (currentAccountId !== undefined) {
    try { sessionStorage.removeItem("skohr_admin_drafts"); } catch { /* unavailable storage */ }
  }
  currentAccountId = accountId;
  appliedEpoch = null;
  ready = false;
  payload = null;
  generation += 1;
}

// Sync triggers (R12): sign-in is driven by the caller (App.jsx, once a
// session resolves); the rest live here.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") syncAppClock();
  });
}

if (typeof window !== "undefined") {
  setInterval(() => {
    if (typeof document === "undefined" || document.visibilityState === "visible") {
      syncAppClock();
    }
  }, 60000);

  // A reset applied in one tab reaches every other open tab through its
  // localStorage write on the epoch key (R13).
  window.addEventListener("storage", (event) => {
    if (event.key === epochKey()) syncAppClock();
  });
}
